import React, { useEffect, useState } from 'react';
import { db } from '../config/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import NavBar from '../components/NavBar';

export default function Dashboard() {
  const [scans, setScans] = useState([]);
  const [leads, setLeads] = useState([]);
  const [listings, setListings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const listingsSnap = await getDocs(collection(db, 'listings'));
      const listingsMap = {};
      listingsSnap.docs.forEach(doc => { listingsMap[doc.id] = doc.data(); });
      setListings(listingsMap);

      const scansSnap = await getDocs(query(collection(db, 'scans'), orderBy('timestamp', 'desc')));
      setScans(scansSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const leadsSnap = await getDocs(query(collection(db, 'leads'), orderBy('submittedAt', 'desc')));
      setLeads(leadsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      setLoading(false);
    };
    fetchData();
  }, []);

  // Scans by listing
  const scansByListing = {};
  scans.forEach(scan => {
    scansByListing[scan.listingId] = (scansByListing[scan.listingId] || 0) + 1;
  });

  // Leads by listing
  const leadsByListing = {};
  leads.forEach(lead => {
    leadsByListing[lead.listingId] = (leadsByListing[lead.listingId] || 0) + 1;
  });

  // Last scan per listing
  const lastScanByListing = {};
  scans.forEach(scan => {
    if (!lastScanByListing[scan.listingId] && scan.timestamp) {
      lastScanByListing[scan.listingId] = scan.timestamp.toDate();
    }
  });

  // Days since last scan
  const daysSince = (date) => {
    if (!date) return null;
    const diff = Date.now() - date.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  // Scans by hour
  const scansByHour = Array(24).fill(0);
  scans.forEach(scan => { if (scan.hour !== undefined) scansByHour[scan.hour]++; });

  // Scans by day of week
  const daysOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const scansByDay = {};
  daysOrder.forEach(d => { scansByDay[d] = 0; });
  scans.forEach(scan => { if (scan.dayOfWeek) scansByDay[scan.dayOfWeek]++; });

  // Ranked listings
  const rankedListings = Object.keys(scansByListing).sort(
    (a, b) => scansByListing[b] - scansByListing[a]
  );

  const maxScans = Math.max(...Object.values(scansByListing), 1);
  const maxHourCount = Math.max(...scansByHour, 1);
  const maxDayCount = Math.max(...Object.values(scansByDay), 1);

  if (loading) return <div style={styles.center}>Loading dashboard...</div>;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📊 Listlytics Dashboard</h1>

      {/* Summary Cards */}
      <div style={styles.cards}>
        <div style={styles.card}>
          <p style={styles.cardNumber}>{scans.length}</p>
          <p style={styles.cardLabel}>Total Scans</p>
        </div>
        <div style={styles.card}>
          <p style={styles.cardNumber}>{leads.length}</p>
          <p style={styles.cardLabel}>Total Leads</p>
        </div>
        <div style={styles.card}>
          <p style={styles.cardNumber}>
            {scans.length > 0 ? Math.round((leads.length / scans.length) * 100) : 0}%
          </p>
          <p style={styles.cardLabel}>Conversion</p>
        </div>
      </div>

      {/* Listing Performance Ranking */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🏆 Listing Performance</h2>
        {rankedListings.map((listingId, index) => {
          const scanCount = scansByListing[listingId] || 0;
          const leadCount = leadsByListing[listingId] || 0;
          const conversion = scanCount > 0 ? Math.round((leadCount / scanCount) * 100) : 0;
          const days = daysSince(lastScanByListing[listingId]);
          const isGold = index === 0;

          return (
            <div key={listingId} style={{
              ...styles.listingCard,
              borderLeft: `4px solid ${isGold ? '#f5a623' : '#2E86AB'}`
            }}>
              <div style={styles.listingHeader}>
                <span style={styles.rank}>{isGold ? '🥇' : `#${index + 1}`}</span>
                <p style={styles.listingAddress}>
                  {listings[listingId]?.address || 'Unknown listing'}
                </p>
              </div>
              <div style={styles.listingStats}>
                <span style={styles.stat}>👁 {scanCount} scans</span>
                <span style={styles.stat}>👤 {leadCount} leads</span>
                <span style={styles.stat}>📈 {conversion}% CVR</span>
              </div>
              <div style={styles.barTrack}>
                <div style={{
                  ...styles.barFill,
                  width: `${(scanCount / maxScans) * 100}%`,
                  background: isGold ? '#f5a623' : '#2E86AB'
                }} />
              </div>
              {days !== null && (
                <p style={{
                  ...styles.lastScan,
                  color: days === 0 ? 'green' : days <= 2 ? '#f5a623' : '#e74c3c'
                }}>
                  {days === 0 ? '🟢 Scanned today' :
                   days === 1 ? '🟡 Last scanned yesterday' :
                   `🔴 No scans in ${days} days`}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Peak Hours Chart */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🕐 Peak Hours</h2>
        <div style={styles.chartRow}>
          {scansByHour.map((count, hour) => (
            count > 0 && (
              <div key={hour} style={styles.barItem}>
                <div style={{
                  ...styles.barVertical,
                  height: `${Math.max((count / maxHourCount) * 80, 4)}px`
                }} />
                <p style={styles.barLabel}>{hour}</p>
                <p style={styles.barCount}>{count}</p>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Peak Days Chart */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>📅 Peak Days</h2>
        <div style={styles.chartRow}>
          {daysOrder.map(day => (
            scansByDay[day] > 0 && (
              <div key={day} style={styles.barItem}>
                <div style={{
                  ...styles.barVertical,
                  height: `${Math.max((scansByDay[day] / maxDayCount) * 80, 4)}px`,
                  background: '#7B2D8B'
                }} />
                <p style={styles.barLabel}>{day}</p>
                <p style={styles.barCount}>{scansByDay[day]}</p>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Leads List */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>👤 Leads</h2>
        {leads.length === 0 ? (
          <p style={styles.empty}>No leads yet.</p>
        ) : (
          leads.map(lead => {
            const submittedAt = lead.submittedAt?.toDate();
            const minsAgo = submittedAt
              ? Math.floor((Date.now() - submittedAt.getTime()) / 60000)
              : null;
            const timeLabel = minsAgo !== null
              ? minsAgo < 60 ? `🔥 ${minsAgo} mins ago`
              : minsAgo < 1440 ? `${Math.floor(minsAgo / 60)}h ago`
              : `${Math.floor(minsAgo / 1440)}d ago`
              : '';

            return (
              <div key={lead.id} style={styles.leadCard}>
                <div style={styles.leadHeader}>
                  <p style={styles.leadName}>{lead.name}</p>
                  <span style={styles.timeAgo}>{timeLabel}</span>
                </div>
                <p style={styles.leadContact}>{lead.email}</p>
                <p style={styles.leadContact}>{lead.phone}</p>
                {lead.message && <p style={styles.leadMessage}>"{lead.message}"</p>}
                <p style={styles.leadListing}>
                  🏠 {listings[lead.listingId]?.address || lead.listingId}
                </p>
              </div>
            );
          })
        )}
      </div>
      <NavBar />
    </div>
  );
}

const styles = {
  container: { maxWidth: 480, margin: '0 auto', padding: 20, fontFamily: 'sans-serif', paddingBottom: 40 },
  center: { textAlign: 'center', marginTop: 100, fontSize: 18 },
  title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20 },
  cards: { display: 'flex', gap: 12, marginBottom: 24 },
  card: { flex: 1, background: '#2E86AB', borderRadius: 12, padding: 16, textAlign: 'center' },
  cardNumber: { fontSize: 28, fontWeight: 'bold', color: '#fff', margin: '0 0 4px' },
  cardLabel: { fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: 0 },
  section: { marginBottom: 28 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 12 },
  listingCard: { background: '#f9f9f9', borderRadius: 10, padding: 14, marginBottom: 10 },
  listingHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  rank: { fontSize: 18 },
  listingAddress: { fontSize: 14, fontWeight: 'bold', margin: 0 },
  listingStats: { display: 'flex', gap: 12, marginBottom: 8 },
  stat: { fontSize: 13, color: '#555' },
  barTrack: { background: '#eee', borderRadius: 4, height: 8 },
  barFill: { borderRadius: 4, height: 8 },
  lastScan: { fontSize: 12, margin: '8px 0 0', fontWeight: 'bold' },
  chartRow: { display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' },
  barItem: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  barVertical: { width: 28, background: '#2E86AB', borderRadius: 4 },
  barLabel: { fontSize: 10, color: '#999', margin: '4px 0 0' },
  barCount: { fontSize: 12, fontWeight: 'bold', margin: 0 },
  leadCard: { border: '1px solid #eee', borderRadius: 10, padding: 14, marginBottom: 10 },
  leadHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  leadName: { fontSize: 16, fontWeight: 'bold', margin: 0 },
  timeAgo: { fontSize: 12, color: '#e74c3c', fontWeight: 'bold' },
  leadContact: { fontSize: 14, color: '#555', margin: '0 0 2px' },
  leadMessage: { fontSize: 14, color: '#777', fontStyle: 'italic', margin: '6px 0' },
  leadListing: { fontSize: 12, color: '#2E86AB', margin: '6px 0 0' },
  empty: { color: '#999', fontStyle: 'italic' }
};


